import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Button, Typography, Box, TextField, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Switch, FormControlLabel, Alert
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Gavel as GavelIcon
} from '@mui/icons-material';
import api from '../services/api';

function TipoAcaoLista() {
    const navigate = useNavigate();
    const [tipoAcaoList, setTipoAcaoList] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ valor: '', ativo: true, ordem: 0 });
    const [error, setError] = useState('');

    useEffect(() => {
        loadTipoAcaoList();
    }, []);

    const loadTipoAcaoList = async () => {
        setLoading(true);
        try {
            const response = await api.get('/tipo-acao-lista');
            setTipoAcaoList(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar lista de tipos de ação:', error);
            setError('Erro ao carregar lista de tipos de ação');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({ valor: item.valor, ativo: item.ativo === 1, ordem: item.ordem || 0 });
        } else {
            setEditingItem(null);
            setFormData({ valor: '', ativo: true, ordem: 0 });
        }
        setOpenDialog(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({ valor: '', ativo: true, ordem: 0 });
        setError('');
    };

    const handleSave = async () => {
        if (!formData.valor.trim()) {
            setError('Valor é obrigatório');
            return;
        }

        try {
            if (editingItem) {
                await api.put(`/tipo-acao-lista/${editingItem.id}`, formData);
            } else {
                await api.post('/tipo-acao-lista', formData);
            }
            handleCloseDialog();
            loadTipoAcaoList();
        } catch (error) {
            console.error('Erro ao salvar tipo de ação:', error);
            setError(error.response?.data?.error || 'Erro ao salvar tipo de ação');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este tipo de ação?')) {
            try {
                await api.delete(`/tipo-acao-lista/${id}`);
                loadTipoAcaoList();
            } catch (error) {
                console.error('Erro ao deletar tipo de ação:', error);
                alert('Erro ao deletar tipo de ação: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const tipoAcaoFiltrados = tipoAcaoList.filter(t => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return t.valor.toLowerCase().includes(searchLower);
    });

    return (
        <Container>
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <GavelIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Tipo de Ação
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Gerencie os tipos de ação disponíveis (Previdenciárias, Trabalhistas, Criminais, Cíveis, Diversas)
                </Typography>
            </Box>

            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Novo Tipo de Ação
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Buscar tipo de ação..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Valor</TableCell>
                            <TableCell align="center">Ativo</TableCell>
                            <TableCell align="center">Ordem</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">Carregando...</TableCell>
                            </TableRow>
                        ) : tipoAcaoFiltrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {search ? 'Nenhum tipo de ação encontrado' : 'Nenhum tipo de ação cadastrado'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            tipoAcaoFiltrados.map((tipoAcao) => (
                                <TableRow key={tipoAcao.id} hover>
                                    <TableCell>{tipoAcao.valor}</TableCell>
                                    <TableCell align="center">
                                        {tipoAcao.ativo === 1 ? '✓' : '✗'}
                                    </TableCell>
                                    <TableCell align="center">{tipoAcao.ordem || 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={() => handleOpenDialog(tipoAcao)}
                                            color="primary"
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(tipoAcao.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingItem ? 'Editar Tipo de Ação' : 'Novo Tipo de Ação'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Valor"
                            value={formData.valor}
                            onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Ordem"
                            type="number"
                            value={formData.ordem}
                            onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                            margin="normal"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.ativo}
                                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                                />
                            }
                            label="Ativo"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default TipoAcaoLista;

